<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import AuroraBg from '@/components/AuroraBg.vue';
import AuroraShaderBg from '@/components/AuroraShaderBg.vue';
import type { ShaderThemeName } from '@/config/shader-themes';
import Grain from '@/components/Grain.vue';
import TopNav from '@/components/TopNav.vue';
import Footer from '@/components/Footer.vue';
import { useSiteConfig } from '@/composables/useSiteConfig';
import { useLang } from '@/composables/useLang';
import { useSessionKeepalive } from '@/composables/useSessionKeepalive';
import { useBgPreference } from '@/composables/useBgPreference';
import { useTextSelect } from '@/composables/useTextSelect';
import { useReadingPrefs } from '@/composables/useReadingPrefs';
import { useThemeTokens } from '@/composables/useThemeTokens';
import { useThemeSkin } from '@/composables/useThemeSkin';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const { load, config } = useSiteConfig();
const { features } = useFeatureFlags();
// shader 主题解析 (含 auto 跟随系统外观) 在 composable 内完成
const { bgMode, effectiveShaderTheme, applyOwnerDefault: applyBgDefault } = useBgPreference();
const { applyOwnerDefault: applyTextSelectDefault } = useTextSelect(); // body class 已在模块加载生效
const { applyOwnerDefault: applyReadingDefault } = useReadingPrefs(); // CSS 变量已在模块加载生效
const { applyAccentPreset, applyDensity } = useThemeTokens();
// 整站主题皮肤 (aurora/polar)。bgMode(熔暮/破晓/子夜/自动) 与主题正交:
// polar 时把当前档映射到极夜冷色版 (dusk→polar-dusk ...), 否则用暖色档。classic 不渲染 shader 不受影响。
const { themeSkin, applyOwnerDefault: applyThemeSkinDefault } = useThemeSkin();
const shaderTheme = computed<ShaderThemeName>(() =>
  themeSkin.value === 'polar'
    ? (`polar-${effectiveShaderTheme.value}` as ShaderThemeName)
    : effectiveShaderTheme.value,
);
const { isZh } = useLang();

// 两层模型: config 异步晚于 composable 初始化, 落地后套用站长默认 —
// applyOwnerDefault 内部只对"访客从没手动设过 (localStorage 无键)"的项生效, 不覆盖访客已存偏好
watch(
  config,
  (cfg) => {
    if (!cfg) return;
    applyThemeSkinDefault(cfg.defaultThemeSkin);
    applyBgDefault(cfg.defaultBgMode);
    applyTextSelectDefault(cfg.defaultTextSelect);
    applyReadingDefault({
      font: cfg.defaultReadingFont,
      line: cfg.defaultReadingLine,
      width: cfg.defaultReadingWidth,
    });
    // polar 主题自带霓虹 accent (CSS 变量), 跳过暖色 accent 内联注入 (否则盖掉霓虹)
    if (themeSkin.value !== 'polar') applyAccentPreset(cfg.accentPreset);
    applyDensity(cfg.densityPreset);
  },
  { immediate: true },
);
const route = useRoute();

// 登录态保活: 续 access token 让私密内容浏览不被 15min 过期打断 (匿名访客无影响)
useSessionKeepalive();

// ── Konami code → Aurora 风暴（彩蛋 C）──
//   ↑ ↑ ↓ ↓ ← → ← → B A
//   触发后 5 秒：blob 饱和+加速 + 整屏 flash + 角落 toast
const KONAMI: readonly string[] = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'b',
  'a',
];
const stormActive = ref(false);
const stormFlash = ref(false);
let konamiBuf: string[] = [];
let stormTimer: ReturnType<typeof setTimeout> | null = null;
let flashTimer: ReturnType<typeof setTimeout> | null = null;

function onKeyForKonami(e: KeyboardEvent) {
  if (!features.value.konami) return; // 站长关了彩蛋
  // 在输入框 / textarea / contenteditable 里按方向键不算（避免误触发）
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  konamiBuf.push(key);
  if (konamiBuf.length > KONAMI.length) konamiBuf.shift();
  if (konamiBuf.length === KONAMI.length && konamiBuf.every((k, i) => k === KONAMI[i])) {
    konamiBuf = [];
    triggerStorm();
  }
}

function triggerStorm() {
  if (stormActive.value) return;
  stormActive.value = true;
  stormFlash.value = true;
  if (flashTimer) clearTimeout(flashTimer);
  flashTimer = setTimeout(() => (stormFlash.value = false), 700); // flash 单帧
  if (stormTimer) clearTimeout(stormTimer);
  stormTimer = setTimeout(() => (stormActive.value = false), 5000);
}

onMounted(() => {
  load();
  window.addEventListener('keydown', onKeyForKonami);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeyForKonami);
  if (stormTimer) clearTimeout(stormTimer);
  if (flashTimer) clearTimeout(flashTimer);
});

// 同步 <title> + <html lang>。不依赖 route.params —— title-map 只看 route.name
watch(
  [() => route.name, config, isZh],
  () => {
    const lang = isZh.value ? 'zh-Hans' : 'en';
    document.documentElement.lang = lang;

    // 自定义页面标题由 Page.vue 自管（页面具体名）；App.vue 只设 <html lang>，不覆盖 title
    if (route.name === 'page') return;

    const siteName = config.value?.siteName ?? 's-blog';
    const subtitle = config.value?.siteSubtitle ?? '';
    const pageTitleMap: Record<string, { zh: string; en: string }> = {
      home: { zh: '', en: '' },
      blog: { zh: '博客', en: 'Blog' },
      post: { zh: '文章', en: 'Post' },
      notes: { zh: '碎片', en: 'Notes' },
      note: { zh: '碎片', en: 'Note' },
      now: { zh: '此刻', en: 'Now' },
      works: { zh: '作品', en: 'Works' },
      work: { zh: '作品', en: 'Work' },
      skills: { zh: 'Skills', en: 'Skills' },
      skill: { zh: 'Skill', en: 'Skill' },
      mcps: { zh: 'MCP 目录', en: 'MCP directory' },
      'mcp-catalog': { zh: 'MCP', en: 'MCP' },
      about: { zh: '关于', en: 'About' },
      wpm: { zh: '打字闯关', en: 'WPM' },
      settings: { zh: '设置', en: 'Settings' },
      'not-found': { zh: '页面不存在', en: 'Not found' },
    };
    const seg = (route.name && typeof route.name === 'string'
      ? pageTitleMap[route.name]
      : undefined) ?? {
      zh: '',
      en: '',
    };
    const segText = isZh.value ? seg.zh : seg.en;
    const tail = subtitle ? ` · ${subtitle}` : '';
    document.title = segText ? `${segText} · ${siteName}` : `${siteName}${tail}`;
  },
  { immediate: true },
);
</script>

<template>
  <!-- 背景按 /settings 偏好切换: shader 三主题 (内部含自动回退) / 经典 CSS 光斑 -->
  <AuroraShaderBg v-if="bgMode !== 'classic'" :theme="shaderTheme" :storm="stormActive" />
  <AuroraBg v-else :storm="stormActive" />
  <Grain />
  <!-- Skip link：键盘 Tab 第一项时显示，Enter 跳到正文，避免每次从顶部 nav 逐项 tab -->
  <a class="skip-link" href="#main-content">{{ isZh ? '跳到主要内容' : 'Skip to main content' }}</a>
  <div class="app">
    <TopNav />
    <main id="main-content" class="app__main">
      <!-- key 用 path（不含 query）：跨页正确 leave/enter，同页 query 变（Notes ?cat=）不重建。
           不用 mode="out-in"：Post 详情页（v-html + 内层 Transition）leave 时 out-in 的
           afterLeave 不触发，下一页会卡在空占位不渲染。注释必须放 Transition 外，
           否则被当成第二个 child 报 "expects exactly one child" -->
      <RouterView v-slot="{ Component }">
        <Transition name="page">
          <component :is="Component" :key="route.path" />
        </Transition>
      </RouterView>
    </main>
    <Footer />
  </div>
  <!-- Konami code 风暴 (彩蛋 C)：整屏 flash + 中央 30 LIVES 横幅 + 角落 toast -->
  <Transition name="flash-fade">
    <div v-if="stormFlash" class="aurora-flash" aria-hidden="true"></div>
  </Transition>
  <Transition name="lives-pop">
    <div v-if="stormActive" class="aurora-lives" aria-hidden="true">
      <div class="aurora-lives__row">
        <span class="aurora-lives__pip">1UP</span>
        <span class="aurora-lives__times">×</span>
        <span class="aurora-lives__num">30</span>
      </div>
      <div class="aurora-lives__label">LIVES UNLOCKED</div>
    </div>
  </Transition>
  <Transition name="toast-slide">
    <div v-if="stormActive" class="aurora-toast" aria-hidden="true">
      🌅 Aurora unlocked · LIVES × 30
    </div>
  </Transition>
</template>

<style scoped>
.app {
  position: relative;
  z-index: 5;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.app__main {
  flex: 1; /* 让 main 撑高，footer 顶到底 */
}

/* Skip link：默认隐藏到屏幕外，:focus 时滑入 */
.skip-link {
  position: fixed;
  top: 12px;
  left: 12px;
  z-index: 200;
  padding: 10px 18px;
  background: var(--aurora-accent);
  color: #fff;
  font-family: var(--font-mono);
  font-size: 13px;
  text-decoration: none;
  border-radius: 8px;
  transform: translateY(calc(-100% - 24px));
  transition: transform 0.2s cubic-bezier(0.2, 0.7, 0.3, 1);
}
.skip-link:focus,
.skip-link:focus-visible {
  transform: translateY(0);
  outline: 2px solid var(--aurora-accent-soft);
  outline-offset: 2px;
}

.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.24s cubic-bezier(0.2, 0.7, 0.3, 1),
    transform 0.24s cubic-bezier(0.2, 0.7, 0.3, 1);
}
.page-enter-from {
  opacity: 0;
  transform: translateY(16px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Konami code Aurora 风暴 ── */
.aurora-flash {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 300;
  background: radial-gradient(
    circle at 50% 40%,
    rgba(255, 220, 200, 0.55) 0%,
    rgba(255, 126, 95, 0.4) 30%,
    rgba(232, 93, 117, 0.2) 60%,
    rgba(26, 11, 31, 0) 90%
  );
  animation: auroraFlash 0.7s ease-out forwards;
}
@keyframes auroraFlash {
  0% {
    opacity: 0;
  }
  20% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
.flash-fade-enter-active,
.flash-fade-leave-active {
  transition: opacity 0.2s;
}
.flash-fade-enter-from,
.flash-fade-leave-to {
  opacity: 0;
}

/* 中央 LIVES × 30 横幅 — 8-bit 红黄配色 + 闪烁 + 缩放进入  */
.aurora-lives {
  position: fixed;
  top: 38%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 305;
  pointer-events: none;
  text-align: center;
  text-shadow:
    0 0 0 #1a0b1f,
    3px 0 0 #1a0b1f,
    -3px 0 0 #1a0b1f,
    0 3px 0 #1a0b1f,
    0 -3px 0 #1a0b1f,
    0 0 24px rgba(255, 126, 95, 0.6);
  /* 持续时长跟 storm 一致 (5s)：进 0.4s、保持、退由 leave 接管 */
  animation: livesIdle 5s steps(1) forwards;
}
.aurora-lives__row {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 18px;
  font-family: var(--font-mono);
  font-weight: 800;
  letter-spacing: 4px;
}
.aurora-lives__pip {
  font-size: 28px;
  color: #ffd66b;
  /* 8-bit 闪烁，每 0.3s 一次 */
  animation: pipBlink 0.6s steps(1) infinite;
}
.aurora-lives__times {
  font-size: 56px;
  color: #fff5e8;
  font-weight: 700;
}
.aurora-lives__num {
  font-size: 88px;
  color: #ff7e5f;
  line-height: 1;
}
.aurora-lives__label {
  margin-top: 12px;
  font-family: var(--font-mono);
  font-size: 14px;
  letter-spacing: 6px;
  color: #fff5e8;
  font-weight: 600;
}
@keyframes livesIdle {
  /* 让 Vue Transition 控制 enter / leave；这里只确保整体 visibility */
  0%,
  100% {
    opacity: 1;
  }
}
@keyframes pipBlink {
  0%,
  49% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0.25;
  }
}
.lives-pop-enter-active {
  transition:
    transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1.4),
    opacity 0.3s ease;
}
.lives-pop-leave-active {
  transition:
    transform 0.4s ease,
    opacity 0.4s ease;
}
.lives-pop-enter-from {
  transform: translate(-50%, -50%) scale(0.4);
  opacity: 0;
}
.lives-pop-leave-to {
  transform: translate(-50%, -60%) scale(1.05);
  opacity: 0;
}

.aurora-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 310;
  padding: 12px 18px;
  border-radius: 999px;
  background: rgba(26, 11, 31, 0.7);
  border: 1px solid rgba(255, 200, 180, 0.25);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: var(--aurora-text);
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.5px;
  box-shadow: 0 10px 40px rgba(255, 126, 95, 0.25);
  pointer-events: none;
}
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition:
    transform 0.3s cubic-bezier(0.2, 0.7, 0.3, 1),
    opacity 0.3s;
}
.toast-slide-enter-from,
.toast-slide-leave-to {
  transform: translateY(12px);
  opacity: 0;
}
</style>
